import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Activity, MailCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError } from "../lib/api";
import useSeo from "../hooks/useSeo";

const Shell = ({ title, subtitle, children, testid }) => (
  <div className="max-w-md mx-auto px-5 sm:px-6 py-16" data-testid={testid}>
    <div className="card-dark p-8 sm:p-10">
      <span className="badge-lime inline-flex items-center gap-2">
        <Activity className="w-3.5 h-3.5" /> SportScience Academy
      </span>
      <h1 className="font-heading font-black text-3xl sm:text-4xl mt-6 text-[#0B1B3A]">{title}</h1>
      <p className="text-[#64748B] mt-3 text-sm">{subtitle}</p>
      {children}
    </div>
  </div>
);

export function ForgotPassword() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useSeo({ lang, title: t("auth.forgot.title"), description: t("auth.forgot.subtitle"), noindex: true });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, lang });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent)
    return (
      <Shell title={t("auth.forgot.sentTitle")} subtitle={t("auth.forgot.sentBody")} testid="forgot-password-sent">
        <MailCheck className="w-10 h-10 text-[#1D4ED8] mt-6" />
        <Link to="/login" className="btn-blue mt-8 inline-flex" data-testid="forgot-back-to-login">
          {t("auth.login.submit")}
        </Link>
      </Shell>
    );

  return (
    <Shell title={t("auth.forgot.title")} subtitle={t("auth.forgot.subtitle")} testid="forgot-password-page">
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-password-form">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#94A3B8]">{t("auth.email")}</label>
          <input
            data-testid="forgot-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-flat mt-2"
            placeholder="you@example.com"
          />
        </div>
        {error && (
          <p className="text-sm text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-3" data-testid="forgot-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} className="btn-blue w-full justify-center" data-testid="forgot-submit-button">
          {loading ? t("common.loading") : t("auth.forgot.submit")}
        </button>
      </form>
      <p className="text-sm text-[#64748B] mt-6">
        <Link to="/login" className="font-bold text-[#1D4ED8]" data-testid="forgot-login-link">
          {t("auth.hasAccount")} {t("auth.login.submit")}
        </Link>
      </p>
    </Shell>
  );
}

export function ResetPassword() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useSeo({ lang, title: t("auth.reset.title"), description: t("auth.reset.subtitle"), noindex: true });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("auth.reset.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      navigate("/login?reset=1");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token)
    return (
      <Shell title={t("auth.reset.invalidTitle")} subtitle={t("auth.reset.invalidBody")} testid="reset-password-invalid">
        <Link to="/forgot-password" className="btn-blue mt-8 inline-flex" data-testid="reset-request-again">
          {t("auth.forgot.submit")}
        </Link>
      </Shell>
    );

  return (
    <Shell title={t("auth.reset.title")} subtitle={t("auth.reset.subtitle")} testid="reset-password-page">
      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#94A3B8]">{t("auth.reset.newPassword")}</label>
          <input
            data-testid="reset-password-input"
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-flat mt-2"
            placeholder="••••••••"
          />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#94A3B8]">{t("auth.reset.confirm")}</label>
          <input
            data-testid="reset-confirm-input"
            type="password"
            required
            minLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="input-flat mt-2"
            placeholder="••••••••"
          />
        </div>
        {error && (
          <p className="text-sm text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-4 py-3" data-testid="reset-error">
            {error}
          </p>
        )}
        <button type="submit" disabled={loading} className="btn-blue w-full justify-center" data-testid="reset-submit-button">
          {loading ? t("common.loading") : t("auth.reset.submit")}
        </button>
      </form>
    </Shell>
  );
}
